import { create } from "zustand";

type Theme = "light" | "dark" | "system";

interface UIState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (v: boolean) => void;
  locale: "en" | "hi";
  setLocale: (l: "en" | "hi") => void;
}

const STORAGE_KEY = "pn-ui-state";

function readInitial(): Partial<UIState> {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

export const useUIStore = create<UIState>((set, get) => ({
  theme: "system",
  sidebarOpen: false,
  locale: "en",
  setTheme: (t) => {
    set({ theme: t });
    persist(get());
    applyTheme(t);
  },
  toggleSidebar: () => {
    set((s) => ({ sidebarOpen: !s.sidebarOpen }));
    persist(get());
  },
  setSidebarOpen: (v) => {
    set({ sidebarOpen: v });
    persist(get());
  },
  setLocale: (l) => {
    set({ locale: l });
    persist(get());
  },
}));

function persist(s: UIState) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ theme: s.theme, locale: s.locale }),
    );
  } catch {
    /* ignore */
  }
}

export function applyTheme(t: Theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const dark =
    t === "dark" ||
    (t === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  root.classList.toggle("dark", dark);
}

export function hydrateUIStore() {
  if (typeof window === "undefined") return;
  const persisted = readInitial();
  if (persisted.theme) useUIStore.setState({ theme: persisted.theme });
  if (persisted.locale) useUIStore.setState({ locale: persisted.locale });
  applyTheme(useUIStore.getState().theme);
}
